import { DeviceLabels, MeetingMode } from '@interfaces/chime.enum';
import { useAppState } from '@providers/AppStateProvider';
import type MeetingManager from '@providers/MeetingProvider/MeetingManager';
import type { MeetingManagerJoinOptions } from '@providers/MeetingProvider/types';
import {
  ConsoleLogger,
  LogLevel,
  MeetingSessionConfiguration,
  VideoAdaptiveProbePolicy,
} from 'amazon-chime-sdk-js';
import meetingConfig from 'client/src/config';
import type { MeetingRoomAttendeeDTO } from 'server/model/meeting.room.dto';

const videoAdaptiveLogger = new ConsoleLogger(
  'VideoAdaptiveProbePolicyLogger',
  LogLevel.WARN,
);

export function useMeetingSession(meetingManager: MeetingManager) {
  const { isWebAudioEnabled, setJoinInfo, setRegion, setMeetingMode } =
    useAppState();

  const initialize = async ({
    JoinInfo,
    meetingMode,
  }: MeetingRoomAttendeeDTO & { meetingMode: MeetingMode }) => {
    try {
      setJoinInfo(JoinInfo);

      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        JoinInfo?.Meeting,
        JoinInfo?.Attendee,
      );

      if (
        meetingConfig.postLogger &&
        meetingSessionConfiguration.meetingId &&
        meetingSessionConfiguration.credentials &&
        meetingSessionConfiguration.credentials.attendeeId
      ) {
        const existingMetadata = meetingConfig.postLogger.metadata;
        meetingConfig.postLogger.metadata = {
          ...existingMetadata,
          meetingId: meetingSessionConfiguration.meetingId,
          attendeeId: meetingSessionConfiguration.credentials.attendeeId,
        };
      }

      setRegion(JoinInfo.Meeting.MediaRegion ?? '');

      meetingSessionConfiguration.enableSimulcastForUnifiedPlanChromiumBasedBrowsers = true;
      meetingSessionConfiguration.videoDownlinkBandwidthPolicy =
        new VideoAdaptiveProbePolicy(videoAdaptiveLogger);

      const options: MeetingManagerJoinOptions = {
        deviceLabels: DeviceLabels.AudioAndVideo,
        enableWebAudio: isWebAudioEnabled,
      };

      await meetingManager.join(meetingSessionConfiguration, options);
      setMeetingMode(meetingMode);

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const initializeTester = async () => {
    return initialize({
      JoinInfo: {
        Title: 'test',
        Name: 'test',
        Identity: 'test',
        Meeting: {
          MeetingId: 'test',
          ExternalMeetingId: undefined,
          MediaPlacement: {
            AudioHostUrl: 'test',
            AudioFallbackUrl: 'test',
            ScreenDataUrl: 'test',
            ScreenSharingUrl: 'test',
            ScreenViewingUrl: 'test',
            SignalingUrl: 'test',
            TurnControlUrl: 'test',
          },
          MediaRegion: 'us-east-1',
        },
        Attendee: {
          ExternalUserId: 'test',
          AttendeeId: 'test',
          JoinToken: 'test',
        },
      },
      meetingMode: MeetingMode.Attendee,
    });
  };

  return { initialize, initializeTester };
}
